import {FC, KeyboardEvent, MouseEvent, useMemo} from 'react'
import {Collapse} from 'react-collapse'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'
import InfiniteScroll from 'react-infinite-scroll-component'

import {
  ChevronRightIcon,
  FormCheckbox,
  RenderIf,
  UserIcon,
  TooltipWrapper,
} from 'components/Basic'
import {num0, num5} from 'definitions/constant'
import {
  SSLErrorItemType,
  AccordionItemProps,
  AccordionProps,
  SelectedItemsTypeForActionButton,
} from 'interfaces'
import {checkTextOverflowed} from 'utils'
import {ActionButton} from '../ActionButton'
import Logger from 'services/Logger'
import {useTouchOnly} from 'hooks/useTouchOnly'

interface AccordionTooltipData {
  id?: string
  totalUserCount?: number
  userList?: string[]
}

export const Accordion: FC<AccordionProps> = ({
  items,
  handleChange,
  hasMore = false,
  fetchData,
  isByAppViewSelected,
  updateByPass,
  areMultipleItemsSelected,
  isUpdateBypassLoading,
}) => {
  const {t} = useTranslation(['common'])

  const handleItemChange = (
    updatedItem: SSLErrorItemType,
    updatedItemIndex: number,
  ): void => {
    const itemsCopy = [...items]
    itemsCopy[updatedItemIndex] = updatedItem
    handleChange(itemsCopy)
  }

  return (
    <div
      id="accordion-section"
      data-cy="accordion-section"
      className="mt-1 max-h-60"
    >
      <InfiniteScroll
        dataLength={items?.length}
        next={fetchData}
        hasMore={hasMore}
        loader={
          <h4 className="karla text-sm px-4 py-2">
            {t('LOAD_MORE_SSL_ERRORS_MESSAGE')}
          </h4>
        }
        className="scrollbar-gutter-stable"
        style={{maxHeight: 235, width: '100%'}}
      >
        {items?.map((item, idx) => {
          const id = `accordion-container-${idx}`
          return (
            <AccordionItem
              {...item}
              id={id}
              key={id}
              isLast={idx === items.length - 1}
              onItemChange={updatedItem => handleItemChange(updatedItem, idx)}
              areMultipleSelected={areMultipleItemsSelected}
              isByAppViewSelected={isByAppViewSelected}
              updateByPass={updateByPass}
              isUpdateBypassLoading={isUpdateBypassLoading}
            />
          )
        })}
      </InfiniteScroll>
      {/* Only render a single tooltip for the whole accordion and fill data accordingly for each accordion item */}
      <TooltipWrapper
        id="user-list-tooltip"
        noArrow
        place="top-end"
        noPadding
        render={({content}) => {
          // Parse the tooltip content back into an object
          let tooltipData = {} as AccordionTooltipData
          try {
            tooltipData = content ? JSON.parse(content) : {}
          } catch (error: unknown) {
            Logger.LogError(error as Error)
            console.error('Failed to parse tooltip content:', error)
          }

          const {id, totalUserCount = 0, userList = []} = tooltipData
          const hasMoreThanFiveUsers = totalUserCount > num5

          return (
            <div
              id={id}
              className="bg-gray-1400 px-3 py-2 karla text-xs rounded-md overflow-hidden"
            >
              {userList
                ?.slice(num0, num5)
                .map((userItem: string, idx: number) => (
                  <p key={`user-item-${idx}`}>{userItem}</p>
                ))}
              <RenderIf isTrue={hasMoreThanFiveUsers}>
                <p>{t('AND_NUM_MORE', {num: totalUserCount - num5})}</p>
              </RenderIf>
            </div>
          )
        }}
      />
    </div>
  )
}

const AccordionItem: FC<AccordionItemProps> = ({
  id,
  areMultipleSelected,
  isLast,
  isUpdateBypassLoading,
  isByAppViewSelected,
  onItemChange,
  updateByPass,
  ...rest
}) => {
  const {
    processName,
    errorCount,
    childItems,
    childSelection,
    userList = [],
    totalUserCount,
    expand,
    isSelected,
  } = rest

  const errorItemIndex = id.split('-')?.pop()
  const sslErrorCheckboxIdPrefix = `ssl-error-checkbox-${errorItemIndex}-${processName}`
  const sslErrorUserListIdPrefix = `ssl-error-${errorItemIndex}-${processName}`

  const hasUserList = userList?.length > 0
  const isTouchOnly = useTouchOnly()
  const isSingleItemSelected = useMemo(() => {
    if (isSelected) {
      return true
    }
    return childItems?.some(childItem => childItem.isSelected)
  }, [isSelected, childItems])

  const onAccordionHeadClick = (newExpandState: boolean): void => {
    const accordionItemCopy = {
      ...rest,
      expand: newExpandState,
    }
    onItemChange(accordionItemCopy)
  }
  const onAccordionHeadMouseClick = (
    event: MouseEvent<HTMLDivElement>,
  ): void => {
    // Touch screens open the user list on tap; that tap shouldn't also expand
    // the row, which is what a click does on desktop.
    const isUserListTap =
      isTouchOnly &&
      (event.target as HTMLElement).closest(
        '[data-tooltip-id="user-list-tooltip"]',
      )
    if (isUserListTap) return
    onAccordionHeadClick(!expand)
  }
  const onAccordionHeadEnterKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
  ): void => {
    // Check if the key pressed is Enter or Space
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation()
      onAccordionHeadClick(!expand)
    }
  }
  const onParentItemCheckboxClick = (updatedState: boolean): void => {
    const accordionItemCopy = {
      ...rest,
      isSelected: updatedState,
    }
    // if parent checkbox is getting unchecked then uncheck all the child items
    if (accordionItemCopy.childItems?.length) {
      accordionItemCopy.childItems = accordionItemCopy.childItems.map(item => ({
        ...item,
        isSelected: updatedState,
      }))
    }
    onItemChange(accordionItemCopy)
  }
  const onChildItemCheckboxClick = (
    updatedChildState: boolean,
    childIdx: number,
  ): void => {
    const childItemsCopy = rest.childItems.map((item, idx) => {
      if (idx === childIdx) {
        return {
          ...item,
          isSelected: updatedChildState,
        }
      }
      return {...item}
    })
    const accordionItemCopy = {
      ...rest,
      childItems: childItemsCopy,
      isSelected: childItemsCopy.every(item => item.isSelected),
    }

    onItemChange(accordionItemCopy)
  }

  const getSelectedItems = (): SelectedItemsTypeForActionButton => {
    if (isSelected) {
      return {parentItems: [processName], childItems: []}
    } else {
      const selectedChildITems = childItems
        ?.filter(childItem => childItem.isSelected)
        .map(childItem => childItem.url)
      return {childItems: selectedChildITems, parentItems: []}
    }
  }

  return (
    <div
      id={id}
      data-cy={id}
      className={classNames('px-6 pt-1 hover:bg-white/[.075]', {
        'bg-white/[.075]': isSingleItemSelected,
        'mb-8': isLast && areMultipleSelected,
      })}
    >
      <div
        className={classNames('border-b border-white/10', {
          'pb-1': !isSelected || areMultipleSelected,
        })}
      >
        <div
          role="button"
          tabIndex={0}
          id="accordion-head"
          data-cy="accordion-head"
          className="h-5 flex justify-between items-center mobile:h-11"
          onClick={onAccordionHeadMouseClick}
          onKeyDown={onAccordionHeadEnterKeyDown}
        >
          <div
            className={classNames('flex items-center', {
              'w-10/12': hasUserList,
              'w-11/12': !hasUserList,
            })}
          >
            <label
              aria-hidden="true"
              htmlFor={sslErrorCheckboxIdPrefix}
              onClick={e => e.stopPropagation()}
            >
              <FormCheckbox
                id={sslErrorCheckboxIdPrefix}
                className="rounded-sm cursor-pointer"
                checked={isSelected}
                onChange={() => {
                  onParentItemCheckboxClick(!isSelected)
                }}
                defaultColor="transparent"
              />
            </label>
            <div className="flex items-center karla-bold text-xs ml-2 w-full">
              <h4
                aria-hidden="true"
                className="truncate"
                style={{maxWidth: '90%'}}
                onMouseOver={event => {
                  checkTextOverflowed(event)
                }}
              >
                {processName}
              </h4>
              <span className="ml-1">
                <span className="karla text-gray-1100">(</span>
                <span className="karla text-dope-purple-50">{errorCount}</span>
                <span className="karla text-gray-1100">)</span>
              </span>
            </div>
          </div>
          <div className="flex items-center">
            <RenderIf isTrue={hasUserList}>
              <div
                data-tooltip-id="user-list-tooltip"
                data-tooltip-content={JSON.stringify({
                  id: `${sslErrorUserListIdPrefix}-user-list`,
                  totalUserCount,
                  userList,
                })}
                id={`${sslErrorUserListIdPrefix}-user-list-icon`}
                className="flex justify-center items-center h-4 w-4 rounded-full hover:bg-dope-cyan-dark mobile:h-9 mobile:w-9"
              >
                <UserIcon />
              </div>
            </RenderIf>
            <RenderIf isTrue={childItems?.length > 0}>
              <ChevronRightIcon
                classes={classNames('ml-3 mr-1', {
                  'rotate-90': !expand,
                  'rotate-270': expand,
                })}
              />
            </RenderIf>
          </div>
        </div>
        <Collapse isOpened={expand}>
          <div id="accordion-body" data-cy="accordion-body">
            {childItems.map((child, childIdx) => {
              const childCheckboxId = `${sslErrorCheckboxIdPrefix}-child-${childIdx}`
              return (
                <div
                  data-cy={childCheckboxId}
                  key={`child-url-${childIdx}`}
                  className="flex items-center px-6 mt-2 w-full"
                >
                  <RenderIf isTrue={childSelection}>
                    <label htmlFor={childCheckboxId}>
                      <FormCheckbox
                        id={childCheckboxId}
                        className="rounded-sm cursor-pointer mr-2"
                        checked={child.isSelected}
                        onChange={() =>
                          onChildItemCheckboxClick(!child.isSelected, childIdx)
                        }
                        defaultColor="transparent"
                      />
                    </label>
                  </RenderIf>
                  <div className="flex items-center karla text-xs text-gray-1000 w-full">
                    <p
                      aria-hidden="true"
                      className="truncate"
                      style={{maxWidth: '90%'}}
                      onMouseOver={event => {
                        checkTextOverflowed(event)
                      }}
                    >{`${child.url}`}</p>
                    <RenderIf isTrue={child.count > 1}>
                      <span className="ml-1">{`(${child.count})`}</span>
                    </RenderIf>
                  </div>
                </div>
              )
            })}
          </div>
        </Collapse>
        <RenderIf isTrue={isSingleItemSelected && !areMultipleSelected}>
          <div className="mt-2">
            <ActionButton
              isByAppViewSelected={isByAppViewSelected}
              selectedItems={getSelectedItems()}
              handler={updateByPass}
              isLoading={isUpdateBypassLoading}
            />
          </div>
        </RenderIf>
      </div>
    </div>
  )
}
